import * as React from "react";
import styled, { css } from "styled-components";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import { LoadingState } from "../states/LoadingState";
import { MediaQueryWrapper, style } from "../style";
import { Row } from "./Table";

export type HeaderProps = {
  title?: string;
  children?: React.ReactNode;
  flex?: number | string;
  isSelected?: boolean;
  onClick?: () => any;
  isLoading?: boolean;
  sortDirection?: string;
  isSortable?: boolean;
  noDropdown?: boolean;
  tooltip?: string;
  action?: ({
    updatedSortDirection,
    onComplete,
    startLoading,
    onError,
  }: {
    updatedSortDirection: string;
    onComplete: () => any;
    startLoading: () => any;
    onError: () => any;
  }) => void;
};

const SORT_DIRECTION_DESC = "desc";
const SORT_DIRECTION_ASC = "asc";

export const Head = styled.div<{ firstColumnOnlyBreakpoint?: string | MediaQueryWrapper }>`
  display: flex;
  border: ${style.vars.borderWidth.base} solid ${style.colors.grey.light};
  border-top-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: ${style.vars.borderRadius.base};

  /* Don't show ellipsis in the header section, but keep flex widths consistent with other rows */
  .with-dropdown & > *:last-child:after {
    content: "";
    width: ${style.vars.space["6"]};
  }

  ${(props) => {
    if (!props.firstColumnOnlyBreakpoint) {
      return;
    }

    return css`
      ${props.firstColumnOnlyBreakpoint} {
        .is-small & {
          display: none;
        }
      }
    `;
  }}
`;

const Title = styled.span`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
`;

const TitleWrap = styled.div`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  max-width: calc(100% - ${style.vars.space["2"]});

  ${Title}:before {
    position: absolute;
    content: "";
    right: -${style.vars.space["4"]};
    bottom: -${style.vars.space["1"]};
    left: -${style.vars.space["2"]};

    transition: all ${style.vars.transitionSpeed.base} ease-out;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  white-space: nowrap;
  width: 100%;
`;

export const TableHeader = styled.div<HeaderProps>`
  display: flex;
  align-items: center;
  font-weight: ${style.vars.fontWeight.bold};
  cursor: ${({ isSortable }) => (isSortable ? "pointer" : "default")};
  padding: ${style.vars.space["1"]} ${style.vars.space["3"]};
  flex: ${({ flex }) => (flex ? flex : 1)};
  min-width: 0;

  ${({ isSelected }) =>
    isSelected &&
    css`
      svg {
        color: ${style.colors.grey.darkest};
      }

      ${Title} {
        &:before {
          border: 1px solid ${style.colors.grey.primary};
          top: -${style.vars.space["1"]};
          border-radius: ${style.vars.borderRadius.base};
        }
      }

      ${HeaderSortIcon} {
        opacity: 1;
      }
    `}

  ${({ isSelected, isSortable }) =>
    !isSelected &&
    isSortable &&
    css`
      &:hover {
        svg {
          color: ${style.colors.grey.dark};
        }

        ${Title} {
          &:before {
            height: 1px;
            background-color: ${style.colors.grey.primary};
          }
        }

        ${Title},
        ${HeaderSortIcon} {
          opacity: 1;
        }
      }
    `}
`;

const HeaderSortIcon = styled(Icon)`
  margin: 0 7px;
  color: ${style.colors.grey.primary};
  opacity: 0;
  transition: opacity ${style.vars.transitionSpeed.base} ease-out;
`;

const HeaderLoadingState = styled(LoadingState)`
  vertical-align: middle;
  width: ${style.vars.space["3"]};
  height: ${style.vars.space["3"]};
  margin: 0 ${style.vars.space["1"]};
`;

// wraps the header title and tooltip when tooltip content is provided
const HeaderTitleWithTooltip = styled.div`
  display: flex;
  align-items: baseline;
`;

export const Header: React.FC<HeaderProps> = ({
  children,
  sortDirection = "asc",
  isLoading = false,
  isSelected = false,
  action,
  tooltip,
  ...props
}) => {
  return (
    <TableHeader {...{ isSelected, isSortable: !!action, ...props }}>
      <HeaderContent>
        <TitleWrap>
          <Title>
            {tooltip ? (
              <HeaderTitleWithTooltip>
                {children} <Tooltip.Help content={tooltip} />
              </HeaderTitleWithTooltip>
            ) : (
              children
            )}
          </Title>
        </TitleWrap>

        {action && isLoading && <HeaderLoadingState />}
        {action && !isLoading && sortDirection === SORT_DIRECTION_DESC && <HeaderSortIcon icon="arrow-filter-up" small />}
        {action && !isLoading && sortDirection === SORT_DIRECTION_ASC && <HeaderSortIcon icon="arrow-filter-down" small />}
      </HeaderContent>
    </TableHeader>
  );
};

const updateLoadingState = (headers: any, clickedIndex: any) => {
  return headers.map((header: any, headerIndex: number) => {
    if (clickedIndex === headerIndex) {
      header.isLoading = true;
      header.isSelected = true;
    } else {
      header.isLoading = false;
      header.isSelected = false;
    }

    // Reset all sort arrows to default before setting sortDirection
    header.sortDirection = SORT_DIRECTION_ASC;

    return header;
  });
};

export const makeHeaders = (allHeaders: any, setHeaders: any, noDropdown: any, firstColumnOnlyBreakpoint: any) => {
  const newHeaders = allHeaders.map(({ title, flex, isSelected, sortDirection, action, isLoading, tooltip }: any, i: number) => {
    return (
      <Header
        key={title}
        flex={flex}
        isSelected={isSelected}
        isLoading={isLoading}
        action={action}
        sortDirection={sortDirection}
        tooltip={tooltip}
        onClick={() => {
          if (action) {
            let updatedSortDirection: typeof SORT_DIRECTION_ASC | typeof SORT_DIRECTION_DESC = "desc";

            if (isSelected) {
              updatedSortDirection = sortDirection === SORT_DIRECTION_DESC ? SORT_DIRECTION_ASC : SORT_DIRECTION_DESC;
            } else if (!isSelected) {
              updatedSortDirection = sortDirection === SORT_DIRECTION_DESC ? SORT_DIRECTION_DESC : SORT_DIRECTION_ASC;
            }

            const startLoading = () => {
              const headersLoadingState = updateLoadingState(allHeaders, i);
              setHeaders(headersLoadingState);
            };

            const onComplete = () => {
              allHeaders[i].isLoading = false;
              allHeaders[i].sortDirection = updatedSortDirection;
              setHeaders(allHeaders);
            };

            const onError = () => {
              allHeaders[i].isLoading = false;
              allHeaders[i].isSelected = false;
              setHeaders(allHeaders);
            };

            action({
              updatedSortDirection,
              startLoading,
              onComplete,
              onError,
            });
          }
        }}
      >
        {title}
      </Header>
    );
  });

  return (
    <Head firstColumnOnlyBreakpoint={firstColumnOnlyBreakpoint}>
      <Row>
        {newHeaders}
        {!noDropdown && <Header flex="initial"></Header>}
      </Row>
    </Head>
  );
};
